import {v3} from "./short3.js";
import Maths from "oc-commons/Maths.js";
import Maths3 from "./Maths3.js";
import Commons from "oc-commons/Commons.js";
import Scenes from "./Scenes.js";
import Screens from "./Screens.js";
import MaterialConverter from "./MaterialConverter";

/**
 * Statische Utility-Klasse zu Texturen erstellen und verändern sowie einfache Objekte mit speziellen Texturen erstellen
 */
export default class Textures {

    /**
     * Gibt ein Raster zurück, eine Art Millimeterpapier mit verschiedenen Rasterebenen
     * @param repetition wie oft soll die Textur wiederhohlt werden
     * @param orginalHeight größe der Textur in Px
     * @param backgroundColor Hintergrundfarbe
     * @param raster Array mit verschiedenen Rater-Ebenen: {
     *       parts: 10, // Anzahl der Unterteilungen
     *       color: "#ddd" // Linienfarbe
     *   }
     * @returns {THREE.Texture}
     */
    static createScalePaperTexture(options) {
        options = Commons.defaultsDeepSafe({}, options, {
            repetition: 100,
            orginalHeight: 100,
            backgroundColor: "#eaeaea",
            raster: [{
                parts: 10,
                color: "#ddd"
            }, {
                parts: 1,
                color: "#bbb"
            }]
        });

        var height = Maths.ceilToNextPowerOf(options.orginalHeight, 2);
        var canvas = document.createElement('canvas');
        $(canvas).attr('width', height);
        $(canvas).attr('height', height);
        var texture = new THREE.Texture(canvas);
        texture.anisotropy = Textures.getMaxAnisotropy();
        (function () {
            var ctx = canvas.getContext('2d');

            ctx.clearRect(0, 0, height, height);
            ctx.beginPath();
            ctx.fillStyle = options.backgroundColor;
            ctx.rect(0, 0, height, height);
            ctx.fill();

            function drawLine(x0, y0, x1, y1) {
                ctx.beginPath();
                ctx.moveTo(x0, y0);
                ctx.lineTo(x1, y1);
                ctx.stroke();
            }

            function drawRaster(parts, color) {
                ctx.strokeStyle = color;
                for (var y = 0; y < height; y += height / parts) {
                    drawLine(y, 0, y, height);
                    drawLine(0, y, height, y);
                }
            }

            options.raster.forEach(r=>drawRaster(r.parts, r.color));

            if (texture) {
                texture.needsUpdate = true;
            }
        })();
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(options.repetition, options.repetition);
        texture.width = () => height;
        texture.height = () => height;
        texture.screenSize = v3(options.orginalHeight, options.orginalHeight, 0);
        return texture;
    }

    /**
     * Erzeugt eine Text-Textur
     * @param message Text, der dargestellt werden soll. Die Textur besitzt eine Methode setText, mit welcher der Text
     *     nachträglich geändert werden kann
     * @param orginalHeight die Höhe in Pixel. Die Weite wird entsprechend der Breite von Message berechnet
     * @param pixelPerUnit Wie hoch soll die Auflösung der Textur sein im Vergleich zur Einheit in der 3D-Welt?
     * @param color die Farbe des Textes
     * @param backgroundColor die Hintergrundfarbe
     * @returns {THREE.Texture}
     */
    static createTextTexture(message, options) {
        if (options.supportMultiLine){
            return Textures.createMultiLineTextTexture(message,options);
        }
        options = Commons.defaultsDeepSafe({}, options, {
            orginalHeight: 0.25,
            originalWidth: null,
            textHeightMultiplier: 0.8,
            pixelPerUnit: 100,
            color: "black",
            backgroundColor: "rgba(255,255,255,0.3)",
            textCanvasCallback: (ctx)=>{}
        });
        message = message + '';
        var orginalWidth = options.originalWidth ? options.originalWidth : (options.orginalHeight * message.length/1.5);
        var height = Maths.ceilToNextPowerOf(options.orginalHeight * options.pixelPerUnit, 2);
        var width = Maths.ceilToNextPowerOf(orginalWidth * options.pixelPerUnit, 2);
        //ToDo: Warning wenn width > window.clientWidth
        if (width > window.clientWidth) {
            console.warning('createTextTexture: width > window.clientWidth!', width);
        }
        var canvas = document.createElement('canvas');
        $(canvas).attr('width', width);
        $(canvas).attr('height', height);

        // get size data (height depends only on font size)
        // var metrics = ctx.measureText( message );
        // var textWidth = metrics.width;

        // canvas contents will be used for a texture
        var texture = new THREE.Texture(canvas);
        texture.anisotropy = Textures.getMaxAnisotropy();
        texture.options = options;
        texture.__oldText = null;
        texture.setText = (text) => {
            if (texture.__oldText===text) {
                return;
            }
            texture.__oldText = text;

            var ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, width, height);
            ctx.beginPath();
            ctx.fillStyle = options.backgroundColor;
            ctx.rect(0, 0, width, height);
            ctx.fill();

            ctx.fillStyle = options.color;
            ctx.font = "Bold " + (height * 0.7) + "px Arial";
            if (options.fontLight) {
                ctx.font = " " + (height * 0.7) + "px Arial";
            }
            options.textCanvasCallback(ctx);
            if (ctx.fontFamily) {
                const fontWeight = ctx.fontWeight ? ctx.fontWeight : 'Bold';
                const fontSizeModifier = ctx.fontSizeModifier ? ctx.fontSizeModifier : 0.7;
                const fontFamily = ctx.fontFamily ? ctx.fontFamily : 'Arial';
                ctx.font = fontWeight + " " + (height * fontSizeModifier) + "px '"+fontFamily+"'";
            }
            var tw = ctx.measureText(text).width;
            var x = (width - tw) / 2;
            if (options.justify=='left') {
                x = 0;
            } else if (options.justify=='right') {
                x = width - tw;
            }
            ctx.fillText(text, x, height * options.textHeightMultiplier);

            if (texture) {
                texture.needsUpdate = true;
            }
        };
        texture.setText(message);
        texture.width = () => width;
        texture.height = () => height;
        texture.screenSize = v3(orginalWidth, options.orginalHeight, 0);
        return texture;
    }

    /**
     * Erzeugt eine Text-Textur
     * @param message Text, der dargestellt werden soll. Die Textur besitzt eine Methode setText, mit welcher der Text
     *     nachträglich geändert werden kann
     * @param orginalHeight die Höhe in Pixel. Die Weite wird entsprechend der Breite von Message berechnet
     * @param pixelPerUnit Wie hoch soll die Auflösung der Textur sein im Vergleich zur Einheit in der 3D-Welt?
     * @param color die Farbe des Textes
     * @param backgroundColor die Hintergrundfarbe
     * @returns {THREE.Texture}
     */
    static createMultiLineTextTexture(message, options) {
        options = Commons.defaultsDeepSafe({}, options, {
            orginalHeight: 0.25,
            originalWidth: null,
            pixelPerUnit: 100,
            color: "black",
            backgroundColor: "rgba(255,255,255,0.3)",
            textCanvasCallback: (ctx)=>{},
            textAlign:'center'
        });
        message = message + '';
        let messageparts,linenumbers,originalWidth,totalHeight,height,width,lineheight,maxLineLength,maxLineTextLength;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        // get size data (height depends only on font size)
        // var metrics = ctx.measureText( message );
        // var textWidth = metrics.width;
        // canvas contents will be used for a texture
        const texture = new THREE.Texture(canvas);
        texture.anisotropy = Textures.getMaxAnisotropy();
        texture.options = options;
        texture.__oldText = null;
        texture.setText = (text) => {
            if (texture.__oldText===text) {
                return;
            }
            texture.__oldText = text;

            messageparts=text.split(/\r?\n/);
            console.log('messageparts',messageparts);
            linenumbers=messageparts.length;
            totalHeight=options.orginalHeight*linenumbers;
            maxLineLength=messageparts.reduce((a, b)=>Math.max(a,b.length),0);

            originalWidth = options.originalWidth ? options.originalWidth : (options.orginalHeight*maxLineLength);
            lineheight=options.orginalHeight * options.pixelPerUnit;
            height = Maths.ceilToNextPowerOf(totalHeight * options.pixelPerUnit, 2);
            width = Maths.ceilToNextPowerOf(originalWidth * options.pixelPerUnit, 2);
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
            ctx.beginPath();
            ctx.font = "Bold " + (lineheight * 0.7) + "px Arial";
            maxLineTextLength=messageparts.reduce((a, b)=>Math.max(a,ctx.measureText(b).width),0);
            $(canvas).attr('width', width);
            $(canvas).attr('height', height);
            ctx.fillStyle =options.backgroundColor;
            ctx.rect(0, 0, width, height);
            ctx.fill();
            ctx.fillStyle = options.color;
            console.log(maxLineTextLength);
            options.textCanvasCallback(ctx);
            messageparts.forEach((s,i)=>{
                const tw = ctx.measureText(s).width;
                switch (options.textAlign){
                    case "left":
                        ctx.fillText(s, (width-maxLineTextLength)/2, lineheight * (0.8+i));
                        break;
                    case "right":
                        ctx.fillText(s, (width+maxLineTextLength)/2-tw, lineheight * (0.8+i));
                        break;
                    case "center":
                        ctx.fillText(s, (width - tw) / 2, lineheight * (0.8+i));
                        break;
                }

            });

            if (texture) {
                texture.screenSize=v3(originalWidth, totalHeight, 0);
                texture.printedArea=v3(originalWidth * options.pixelPerUnit,totalHeight * options.pixelPerUnit,0);
                texture.needsUpdate = true;
            }
        };
        texture.setText(message);
        texture.width = () => width;
        texture.height = () => height;
        return texture;
    }


    /**
     * Erzeugt ein Sprite mit einem Text
     * @param message Text, der dargestellt werden soll. Die Textur besitzt eine Methode setText, mit welcher der Text
     *     nachträglich geändert werden kann
     * @param orginalHeight die Höhe in Pixel. Die Weite wird entsprechend der Breite von Message berechnet
     * @param cameraIndependendSize ob das Sprite perspektivisch kleiner wird (false) oder immer gleich groß ist (true)
     * @returns {THREE.Texture}
     */
    static createTextSprite(message, options) {
        options = Commons.defaultsDeepSafe({}, options, {
            orginalHeight: 0.25,
            originalWidth: null,
            pixelPerUnit: 100,
            cameraIndependendSize: true,
            color: "black",
            backgroundColor: "rgba(255,255,255,0.3)",
            offset: v3(),
            depthTest:true,
            moveTextUpinHeightMultiplies: 0,
        });
        var texture = Textures.createTextTexture(message, options);
        var spriteMaterial = new THREE.SpriteMaterial({map: texture,depthTest:options.depthTest});
        var spriteContainer = new THREE.Object3D();
        var sprite = new THREE.Sprite(spriteMaterial);
        spriteContainer.add(sprite);
        sprite.scale.set(texture.width() / (2 * options.pixelPerUnit), texture.height() / (2 * options.pixelPerUnit), 1.0);
        const originalScale = sprite.scale.clone();
        spriteContainer.setText = texture.setText;
        spriteContainer.options = texture.options;
        spriteContainer.name = 'TextSprite: ' + message;

        spriteContainer.getLabelSizeForRayFromMiddle = (start, end, stage) => {
            var direction = end.clone().sub(start);
            var screenPositions = [start, sprite.position, end].map(Screens.screenPosition);
            screenPositions.forEach(v=>v.z = 0);
            var screenDirection = screenPositions[2].clone().sub(screenPositions[0]);
            var labelSize = sprite.screenSize.clone().multiplyScalar(0.5).setZ(1);
            var labelBox = new THREE.Box3(screenPositions[1].clone().sub(labelSize), screenPositions[1].clone().add(labelSize));
            var p1 = new THREE.Ray(screenPositions[0], screenDirection.clone().normalize()).intersectBox(labelBox);
            var p2 = new THREE.Ray(screenPositions[2], screenDirection.clone().normalize().multiplyScalar(-1)).intersectBox(labelBox);
            var labelRayFactor = 1;
            if (p1 != null && p2 != null) {
                var a = p1.distanceTo(p2);
                var b = screenPositions[0].distanceTo(screenPositions[2]) * 0.9 + 10;
                labelRayFactor = a === 0 || a >= b ? 1 : Maths.cutBetween(0, a / b, 1);
            }
            return direction.clone().multiplyScalar(labelRayFactor / 2);
        };

        if (options.cameraIndependendSize) {
            sprite.onBeforeRenderStage = () => {
                if (!STAGE.orthographicMode) {
                    var position = v3().setFromMatrixPosition(sprite.matrixWorld);
                    var sizeOf1 = Screens.visibleSizeInPx(new THREE.Sphere(position, 1));
                    var scale = options.orginalHeight * options.pixelPerUnit / sizeOf1;
                    sprite.scale.set(originalScale.x / originalScale.y * scale, scale, 1);
                    spriteContainer.updateMatrixWorld();
                    if (options.moveTextUpinHeightMultiplies!=0) {
                        const moveUpY = v3(0,options.moveTextUpinHeightMultiplies * texture.screenSize.y * 2 * scale,0);
                        moveUpY.applyQuaternion( spriteContainer.quaternion );
                        sprite.position.copy(moveUpY);
                    }
                }
            }
        }
        return spriteContainer;
    }

    /**
     * Erzeugt eine PlaneGeometrie mit einem Text
     * @param message Text, der dargestellt werden soll. Die Textur besitzt eine Methode setText, mit welcher der Text
     *     nachträglich geändert werden kann
     * @param orginalHeight die Höhe in Pixel. Die Weite wird entsprechend der Breite von Message berechnet
     * @param pixelPerUnit Wie hoch soll die Auflösung der Textur sein im Vergleich zur Einheit in der 3D-Welt?
     * @returns {THREE.Texture}
     */
    static createTextPlane(message, options) {
        options = Commons.defaultsDeepSafe({}, options, {
            orginalHeight: 0.25,
            pixelPerUnit: 100,
            quaternion: Maths3.quaternion.zero,
            offset: v3(),
            depthTest:true

        });
        var texture = Textures.createTextTexture(message, options);
        var geometry = new THREE.PlaneGeometry(texture.width() * options.orginalHeight / texture.height(), options.orginalHeight);
        var material = new THREE.MeshBasicMaterial({map: texture, side: THREE.DoubleSide, transparent: true,depthTest:options.depthTest});
        var plane = new THREE.Mesh(geometry, material);
        plane.name = 'TextPlane: ' + message;
        plane.quaternion.copy(options.quaternion);
        plane.setText = texture.setText;
        plane.options = texture.options;
        plane.screenSize = texture.screenSize;
        plane.getLabelSizeForRayFromMiddle = (start, end, stage) => {
            var box = Scenes.boundingBox(plane);
            var direction = end.clone().sub(start).normalize();
            var p1 = new THREE.Ray(start, direction).intersectBox(box);
            var p2 = new THREE.Ray(end, direction.clone().multiplyScalar(-1)).intersectBox(box);
            if (p1 != null && p2 != null) {
                return p2.sub(p1).multiplyScalar(0.5);
            }
            return end.clone().sub(start).multiplyScalar(0.2);
        };
        return plane;
    }

    /**
     * erzeugt entweder eine Textdarstellung als Sprite oder als Plane
     * @param message
     * @param options
     * @returns {THREE.Texture}
     */
    static createTextSpriteOrPlane(message, options) {
        if (options.textAsSprite) {
            return Textures.createTextSprite(message, options);
        } else {
            return Textures.createTextPlane(message, options);
        }
    }

    /**
     * setzt die Farbe eines Objektes und aller (default) Unterobjekte
     */
    static setColor(object, color, recursive = true) {
        color = color instanceof THREE.Color ? color : new THREE.Color(color);

        const doSetColor = (material)=>{
            if (material.highlightBackup) {
                material.highlightBackup.color = color;
            } else {
                material.color = color;
            }
            return material;
        };

        maybeRecursive(object, recursive, (o)=> {
            Textures._alterMaterial(o,doSetColor);
        });
        return object;
    }


    /**
     * bearbeitet Material oder alle Materialien
     * @param object das Object dessen Material geändert werden soll.
     * @param materialCallback (material):alteredMaterial=>{}
     * @private
     */
    static _alterMaterial(object,materialCallback){
        if (object.material) {
            if (Array.isArray(object.material)) {
                for (let i=0;i<object.material.length;i++) {
                    object.material[i] = materialCallback(object.material[i]);
                }
            }
            else{
                object.material=materialCallback(object.material);
            }
        }
    }

    /**
     * setzt material.color aller (default)  Unterobjekte auf weiss
     */
    static removeColor(object, recursive = true) {
        return Textures.setColor(object, new THREE.Color(0xffffff), recursive);
    }

    /**
     * Hebt ein Objekt farbig hervor.
     * Setzt die Farbe und Opacity eines Objektes und aller Unterobjekte, und merkt sich die vorigen Einstellungen.
     * Diese können mit unhighlight(object) wieder zurückgesetzt werden.
     */
    static highlight(object, options) {
        options = Commons.defaultsDeepSafe({}, options, {
            color: null,
            opacity: null,
            multiplyOpacity: false,
            recursive: true,
            material: null,
        });

        const threejsColor = options.color == null ? null : (options.color instanceof THREE.Color ? options.color : new THREE.Color(options.color));

        const doHighlight=(material)=> {
            let mat = material;
            material = (options.material ? options.material : material).clone();
            material.needsUpdate = true;
            if (mat.highlightBackup) {
                material.highlightBackup = mat.highlightBackup;
                mat.dispose();
            } else {
                material.highlightBackup = mat;
            }
            const backup = material.highlightBackup;

            if (threejsColor == null) {
                if (backup.color !== undefined) {
                    material.color = backup.color;
                }
            } else {
                material.color = threejsColor;
            }
            // node.material.blending=AdditiveBlending führte dazu, das Objekte weiß erscheinen, wenn sie
            // transparent sind. revertOpacity kann das dann auch nicht mehr zurücksetzen
            if (options.opacity != null && material.blending === THREE.NormalBlending) {
                material.opacity = (options.multiplyOpacity ? (backup.opacity != null ? backup.opacity : material.opacity) : 1) * options.opacity;
                if (options.opacity < 1.0 && !material.transparent) {
                    material.transparent = true;
                    material.needsUpdate = true;
                }
            } else {
                if (backup.transparent !== undefined) {
                    material.transparent = backup.transparent;
                    material.needsUpdate = true;
                }
            }
            return material;
        };

        maybeRecursive(object, options.recursive, (o)=> {
            Textures._alterMaterial(o,doHighlight);
        });
        return object;
    }

    /**
     * setzt die Farbe und Opacity eines Objektes und aller Unterobjekte
     * @param color wenn null, dann wird die alte Farbe wiederhergestellt
     */
    static unhighlight(object, recursive = true) {
        const doUnhighlight = (material) => {
            if (material.highlightBackup) {
                material.dispose();
                return material.highlightBackup;
            }
            return material;
        };

        maybeRecursive(object, recursive, (o) => {
            Textures._alterMaterial(o,doUnhighlight);
        });
        return object;
    }

    /**
     * Färbt die vorhandene Textur mit einer Farbe ein
     */
    static colorizeMaterialTexture(material, fillStyle, alpha, compositeOp) {
        const oldMap = material.map;
        material.map = Textures.colorizeTexture(material.map, fillStyle, alpha, compositeOp);
        oldMap.dispose();
        material.map.needsUpdate = true;
        material.needsUpdate = true;
        return material;
    }

    /**
     * Setzt die Transparenz eine Objektes
     * @param object
     * @param opacity
     * @param multiply wenn true multipliziert bei vorhandener Transparenz alten und neuen Wert (=transparente Objekte
     *     bleiben transparent)
     */
    static setOpacity(object, opacity, multiply = false, recursive = true) {
        const doSetOpacity = (material)=>{
            if (material.highlightBackup) {
                const backup = material.highlightBackup;
                backup.opacity = (multiply ? (backup.opacity != null ? backup.opacity : material.opacity) : 1) * opacity;
                backup.transparent = backup.opacity!=1;
            } else {
                material.opacity = (multiply ? material.opacity : 1) * opacity;
                material.transparent = material.opacity!=1;
                material.needsUpdate = true;
            }
            return material;
        }

        maybeRecursive(object, recursive, (o)=> {
            Textures._alterMaterial(o,doSetOpacity);
        });
        return object;
    }

    /**
     * erzeugt eine einfarbige Textur bestehend aus nur einem Pixel
     */
    static createSingleColorTexture(color) {
        return Textures.createTextureFromCanvas(Textures.getSingleColorCanvas(color));
    }

    /** */
    static createTextureFromCanvas(canvas) {
        var texture = new THREE.Texture(canvas);
        texture.needsUpdate = true;
        return texture;
    }

    /**
     * Setzt die Texture auf einem Object
     */
    static setTexture(object, texture, options) {
        options = Commons.defaultsDeepSafe({}, options, {
            recursive: true,
            repeatMirrored: false,
            repeat: null,
            repeatInAbsoluteUnits: false,
            shininess: null,
            removeColor: false,
        });
        maybeRecursive(object, options.recursive, (o)=> {
            if (o.material) {
                if (o.material.map) {
                    o.material.map.dispose();
                }
                o.material.map = texture;
                const map = o.material.map;
                if (options.removeColor) {
                    if (o.material.highlightBackup) {
                        o.material.highlightBackup.color = new THREE.Color(0xffffff);
                    } else {
                        o.material.color = new THREE.Color(0xffffff);
                    }
                }
                if (options.shininess) {
                    o.material.shininess = options.shininess;
                }
                if (map) {
                    if (options.repeatMirrored) {
                        map.wrapS = THREE.MirroredRepeatWrapping;
                        map.wrapT = THREE.MirroredRepeatWrapping;
                    }
                    if (options.repeat) {
                        map.repeat.clone(repeat);
                    }
                    if (options.repeatInAbsoluteUnits) {
                        var box = Scenes.boundingBox(o);
                        var size = box.getSize();
                        var extend = object.userData.extend;
                        if (!extend) extend = ["x", "y"];
                        var rx = size[extend[0]] / map.scale;
                        var ry = size[extend[1]] / map.scale;
                        // doch nicht so toll: klappt natürlich nur für Objekte, die nie gedreht werden...
                        map.repeat.set(rx, ry);
                    }
                    // Results in threejs warning, but prevents flickering...
                    // SBA: mit der Abfrage auf existenz des Images sollte die Warning weg
                    // sein. Habe da jetzt kein zusätzliches Flackern festgestellt
                    if (map.image) {
                        map.needsUpdate = true;
                    }
                }
                // fixme wirklich notwendig?
                o.material.needsUpdate = true;
            }
        });
        return object;
    }

    /**
     * Setzt ein Material in ein Mesh mit der Möglichkeit eines textureOffset
     */
    static setMaterialWithRandomOffsetInto({target,material,textureOffsetRandom}) {
        if (material!==target.material && target.material!=null && target.material.__isRandomMaterial) {
            target.material.dispose();
            if (target.material.map) {
                target.material.map.dispose();
            }
        }
        if (textureOffsetRandom) {
            if (!target.__textureOffset) {
                target.__textureOffset = new THREE.Vector2(Math.random(), Math.random());
            }
            target.material = material.clone();
            target.material.__isRandomMaterial = true;
            const texture = target.material.map = target.material.map.clone();
            texture.offset.copy(target.__textureOffset);
        } else {
            target.material = material;
        }
    }

    /**
     * gibt eine Liste aller Material-Namen eines Objektes zurück
     * @param object
     * @returns {string[]}
     */
    static getMaterialNames(object) {
        return new Set(Textures.getMaterials(object).map(m=>m?.name)).toArray().filter(name=>!Commons.isEmpty(name));
    }

    /**
     * gibt eine Liste aller Materialien eines Objektes zurück
     * @param {*} [object=STAGE.scene]
     * @returns {Array}
     */
    static getMaterials(object=STAGE.scene) {
        const materials = new Set();
        object.traverse(o=>{
            if (o.material) {
                (_.isArray(o.material) ? o.material : [o.material])
                    .forEach(m=>materials.add(m));
            }
        });
        return Array.from(materials);
    }

}


/**
 * erzeugt einen Canvas aus nur einer Farbe
 */
Textures.getSingleColorCanvas = Commons.cache(function (color, size = 1) {
    var canvas = document.createElement('canvas');
    var width = size;
    var height = size;
    $(canvas).attr('width', width);
    $(canvas).attr('height', height);
    var ctx = canvas.getContext('2d');

    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.rect(0, 0, width, height);
    ctx.fill();

    return canvas;
});

/**
 * erzeugt einen Canvas, der einen einfarbigen Kreis zeigt. Der Hintergrund ist transparent.
 */
Textures.getColorCircleCanvas = Commons.cache(function (color, width) {
    width = Maths.ceilToNextPowerOf(width);
    var canvas = document.createElement('canvas');
    $(canvas).attr('width', width);
    $(canvas).attr('height', width);
    var ctx = canvas.getContext('2d');

    ctx.beginPath();
    ctx.fillStyle = "rgba(0,0,0,0)";
    ctx.rect(0, 0, width, width);
    ctx.fill();

    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.arc(width / 2, width / 2, width / 4, 0, 2 * Math.PI, false);
    ctx.fill();

    return canvas;
});

/**
 * Gibt zurück, wie hoch der MaxAnisoptry Wert des WebGL-Renderers ist
 */
Textures.getMaxAnisotropy = _.once(function () {
    return (STAGE.renderer ? STAGE.renderer : new THREE.WebGLRenderer({})).capabilities.getMaxAnisotropy();
});

/**
 * Färbt eine Textur ein
 */
Textures.colorizeTexture = Commons.cache((srcTexture, fillStyle, alpha = 1, compositeOp = 'source-over')=> {

    var originalTexture = () => (srcTexture.sourceTexture == null) ? srcTexture : srcTexture.sourceTexture;

    if (!fillStyle) {
        return originalTexture();
    }

    var srcCanvas = srcTexture.image;
    var dstCanvas = document.createElement('canvas');
    dstCanvas.width = srcCanvas.width;
    dstCanvas.height = srcCanvas.height;

    var ctx = dstCanvas.getContext('2d');
    ctx.drawImage(srcCanvas, 0, 0);
    ctx.globalCompositeOperation = compositeOp;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = fillStyle;
    ctx.fillRect(0, 0, dstCanvas.width, dstCanvas.height);

    var dstTexture = srcTexture.clone();
    dstTexture.image = dstCanvas;

    dstTexture.sourceTexture = originalTexture();

    return dstTexture;
});

/**
 * interne Funktion
 */
function maybeRecursive(object, recursive, callback) {
    if (recursive) {
        object && object.traverse(callback);
    } else {
        callback(object);
    }
}

window.Textures = Textures;
